import AddIcon from "@mui/icons-material/Add";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import { type FC, type MouseEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import type { ShiftRequest } from "../../../utils/models";
import { hasNurseResponse } from "../../../utils/utilFunctions";
import { MedicalDepartmentPostShift } from "../../MedicalDepartmentAdditionsPopup/MedicalDepartmentPostShift/MedicalDepartmentPostShift";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import { MultiSizePopup } from "../../UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../../UIToolkit/Popups/MultiSizePopup/utils";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { useDeleteShift } from "../hooks";
import { MedicalDepartmentRequestItem } from "./MedicalDepartmentRequestItem";

enum MedicalDepartmentRequestFilter {
	All = "all",
	WithResponse = "with_response",
	WithoutResponse = "without_response",
}

type SectionProps = {
	title: string;
	requests: ShiftRequest[];
	isExpanded: boolean;
	onToggle: () => void;
	onDelete: (event: MouseEvent<HTMLElement>, request: ShiftRequest) => void;
};

const RequestSection: FC<SectionProps> = ({ title, requests, isExpanded, onToggle, onDelete }) => {
	const theme = useTheme();
	if (requests.length === 0) return null;

	return (
		<Box sx={{ mb: 2 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: 2,
					backgroundColor: "background.paper",
					borderRadius: 1,
					cursor: "pointer",
					mb: 1,
					"&:hover": { backgroundColor: "action.hover" },
				}}
				data-testid={`medical-request-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
				onClick={onToggle}
			>
				<Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.dark }}>
					{title} ({requests.length})
				</Typography>
				{isExpanded ? <ExpandLess sx={{ color: theme.palette.primary.dark }} /> : <ExpandMore sx={{ color: theme.palette.primary.dark }} />}
			</Box>
			<Collapse in={isExpanded}>
				<AnimatePresence mode="popLayout">
					{requests.map((request, index) => (
						<motion.div
							key={request.id}
							id={`request-${request.id}`}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.3, delay: index * 0.05, ease: "easeOut" }}
						>
							<MedicalDepartmentRequestItem request={request} onDelete={onDelete} />
						</motion.div>
					))}
				</AnimatePresence>
			</Collapse>
		</Box>
	);
};

export const MedicalDepartmentRequestsTab: FC = () => {
	const { t } = useTranslation();
	const { staffRequests } = useRequestsContext();
	const { mutateAsync: deleteShift, isPending: isDeletingShift } = useDeleteShift();
	const today = useMemo(() => DateTime.now(), []);
	const [deleteConfirmation, setDeleteConfirmation] = useState(false);
	const [requestToDelete, setRequestToDelete] = useState<ShiftRequest>();
	const [postShiftOpen, setPostShiftOpen] = useState(false);
	const [selectedFilter, setSelectedFilter] = useState<MedicalDepartmentRequestFilter>(MedicalDepartmentRequestFilter.All);
	const [expandedSections, setExpandedSections] = useState({ currentWeek: true, nextWeek: true, future: false });

	const filteredRequests = useMemo<ShiftRequest[]>(() => {
		switch (selectedFilter) {
			case MedicalDepartmentRequestFilter.WithResponse:
				return staffRequests.filter(hasNurseResponse);
			case MedicalDepartmentRequestFilter.WithoutResponse:
				return staffRequests.filter((req) => !hasNurseResponse(req));
			default:
				return staffRequests;
		}
	}, [staffRequests, selectedFilter]);

	const groupedRequests = useMemo(() => {
		const now = DateTime.now();
		const currentWeekStart = now.startOf("week");
		const nextWeekStart = currentWeekStart.plus({ weeks: 1 });

		const currentWeek: ShiftRequest[] = [];
		const nextWeek: ShiftRequest[] = [];
		const future: ShiftRequest[] = [];

		for (const request of filteredRequests) {
			const requestWeekStart = request.message.shift_start.startOf("week");
			if (requestWeekStart <= currentWeekStart) {
				currentWeek.push(request);
			} else if (requestWeekStart.equals(nextWeekStart)) {
				nextWeek.push(request);
			} else {
				future.push(request);
			}
		}

		return { currentWeek, nextWeek, future };
	}, [filteredRequests]);

	const onDelete = useCallback((event: MouseEvent<HTMLElement>, request: ShiftRequest): void => {
		event.stopPropagation();
		setRequestToDelete(request);
		setDeleteConfirmation(true);
	}, []);

	const handleConfirmDelete = async (): Promise<void> => {
		if (!requestToDelete) return;
		await deleteShift({ shiftId: requestToDelete.id });
		setDeleteConfirmation(false);
		setRequestToDelete(undefined);
	};

	const toggleSection = (section: keyof typeof expandedSections): void => {
		setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
	};

	const filterOptions: FlatSwitchOption<MedicalDepartmentRequestFilter>[] = [
		{ value: MedicalDepartmentRequestFilter.All, label: "All" },
		{ value: MedicalDepartmentRequestFilter.WithResponse, label: "Answered" },
		{ value: MedicalDepartmentRequestFilter.WithoutResponse, label: "Unanswered" },
	];

	const postShiftBody = useMemo(() => <MedicalDepartmentPostShift clickedDate={today} onClose={() => setPostShiftOpen(false)} />, [today]);

	const postShiftBodyRenderer = useCallback(
		(currentSizeState: MultiSizePopupState) => {
			const visible = currentSizeState === MultiSizePopupState.MINIMIZED;
			return (
				<Box
					sx={{
						visibility: visible ? "visible" : "hidden",
						pointerEvents: visible ? "auto" : "none",
						height: visible ? "auto" : 0,
						overflow: "hidden",
					}}
				>
					{postShiftBody}
				</Box>
			);
		},
		[postShiftBody],
	);

	return (
		<Box data-testid="medical-requests-tab-root" sx={{ overflow: "hidden", height: "100%" }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
				<SystemFlatSwitch
					fontSizeOverrides={{ xs: "12px", xl: "12px", xxl: "12px" }}
					maxWidthOverrides={{ xs: "94px", xl: "94px", xxl: "94px" }}
					options={filterOptions}
					activeIndexOrValue={selectedFilter}
					onSwitch={(option) => setSelectedFilter(option.value)}
					data-testid="medical-requests-filter"
				/>
				<SystemButton
					data-testid="medical-requests-new-shift"
					leadingIcon={<AddIcon fontSize="small" />}
					onClick={() => setPostShiftOpen(true)}
					sx={{ height: "32px", fontSize: "12px" }}
				>
					New
				</SystemButton>
			</Box>

			<Box sx={{ overflowY: "auto", height: "calc(100% - 50px)", mt: 1 }}>
				<RequestSection
					title="Current Week"
					requests={groupedRequests.currentWeek}
					isExpanded={expandedSections.currentWeek}
					onToggle={() => toggleSection("currentWeek")}
					onDelete={onDelete}
				/>
				<RequestSection
					title="Next Week"
					requests={groupedRequests.nextWeek}
					isExpanded={expandedSections.nextWeek}
					onToggle={() => toggleSection("nextWeek")}
					onDelete={onDelete}
				/>
				<RequestSection
					title="Future"
					requests={groupedRequests.future}
					isExpanded={expandedSections.future}
					onToggle={() => toggleSection("future")}
					onDelete={onDelete}
				/>
			</Box>

			{postShiftOpen && (
				<MultiSizePopup
					title="Post Shift"
					open={postShiftOpen}
					onClose={() => setPostShiftOpen(false)}
					disableMaximize={true}
					disableMinimize={true}
					keepBodyMounted={true}
					bodyRenderer={postShiftBodyRenderer}
				/>
			)}

			<Dialog data-testid="medical-request-delete-dialog" open={deleteConfirmation} onClose={() => setDeleteConfirmation(false)}>
				<DialogTitle>{t("delete.open.shift.title")}</DialogTitle>
				<DialogContent>{t("delete.open.shift.warning")}</DialogContent>
				<DialogActions>
					<SystemButton
						data-testid="medical-request-delete-cancel-button"
						buttonVariant="secondary"
						onClick={() => setDeleteConfirmation(false)}
						disabled={isDeletingShift}
					>
						{t("cancel")}
					</SystemButton>
					<SystemButton data-testid="medical-request-delete-confirm-button" buttonVariant="delete" onClick={handleConfirmDelete} loading={isDeletingShift}>
						{t("delete.request")}
					</SystemButton>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
